import React, { useCallback, useEffect, useRef } from 'react'
import { Form, message } from 'antd'
import { object, ref, string } from 'yup'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import { Alert, Modal } from 'src/components/Ant'
import { ApplicationState } from 'src/store'
import { changePassword } from 'src/core/store/ducks/me/actions'
import { InputPassword } from 'src/components/Formik'
import { useT } from 'src/core/hooks/use-translation'

interface Props {
  visible: boolean
  toggle: () => void
}

const initialValues = { password: '', passwordConfirmation: '' }

const validationSchema = object().shape({
  password: string()
    .matches(/^[^\s]+$/, {
      message: 'web.side.user.change-password.password.invalid-character',
      excludeEmptyString: true
    })
    .min(6, 'web.side.user.change-password.password.min-length')
    .required(),
  passwordConfirmation: string()
    .oneOf(
      [ref('password')],
      'web.side.user.change-password.retype-password.not-same'
    )
    .required()
})

function ModalChangePassword({ visible, toggle }: Props) {
  const { t } = useT()
  const {
    me: { user, loading, error }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))
  const dispatch = useDispatch()
  const refFormik = useRef(null)
  const handleSubmit = useCallback(({ password }) => {
    dispatch(changePassword(password))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function logout() {
    localStorage.clear()
    window.location.replace('/')
  }

  useEffect(() => {
    if (
      visible &&
      loading?.changePassword === false &&
      error?.changePassword === false
    ) {
      message.success(
        t('common.push-notifications.password-change-successful'),
        5
      )
      toggle()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading?.changePassword, error?.changePassword])

  const titleModal = !user.must_change_password ? (
    'web.side.user.change-password.title'
  ) : (
    <>{`${t('web.side.user.change-password.title')} (${t(
      'common.required'
    )})`}</>
  )
  const cancelText = !user.must_change_password
    ? 'common.actions.cancel'
    : 'web.side.user.logout'
  return (
    <Modal
      cancelText={cancelText}
      closable={!user.must_change_password}
      destroyOnClose
      maskClosable={false}
      okButtonProps={{ loading: loading.changePassword }}
      okText="common.actions.change"
      onCancel={!user.must_change_password ? toggle : logout}
      onOk={() => refFormik.current.submitForm()}
      title={titleModal}
      visible={visible}
      width={400}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={formikProps => (
          <Form
            onSubmit={formikProps.handleSubmit}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                formikProps.handleSubmit()
              }
            }}
          >
            {error?.changePassword && (
              <Form.Item>
                <Alert
                  message="web.side.user.change-password.generic-error"
                  type="error"
                  showIcon
                />
              </Form.Item>
            )}
            <InputPassword
              autoFocus
              formikProps={formikProps}
              label="web.side.user.change-password.password.title"
              name="password"
              placeholder="web.side.user.change-password.password.placeholder"
            />
            <InputPassword
              formikProps={formikProps}
              label="web.side.user.change-password.retype-password.title"
              name="passwordConfirmation"
              placeholder="web.side.user.change-password.retype-password.placeholder"
            />
          </Form>
        )}
        validationSchema={validationSchema}
      />
    </Modal>
  )
}

export default ModalChangePassword
